'use client';

import { useMemo } from 'react';
import { ResponsiveContainer, AreaChart as RAreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { ToolsTranslations } from '@/lib/translations/types';

type ChartPoint = { year: string; Total: number; Return: number };

interface Props {
  startingAmount: number;
  cagr: number | null; // decimal
  years: number;
  translations: ToolsTranslations['cagr'];
  onCagrChange?: (cagr: number) => void;
  currency: string;
}

export default function CagrChart({ startingAmount, cagr, years, translations, onCagrChange, currency }: Props) {
  const data: ChartPoint[] = useMemo(() => {
    const points: ChartPoint[] = [];
    if (cagr === null || years <= 0 || startingAmount <= 0) return points;
    for (let i = 0; i <= years; i++) {
      const total = Math.round(startingAmount * Math.pow(1 + cagr, i));
      const ret = total - startingAmount;
      points.push({ year: `${i}`, Total: total, Return: ret });
    }
    return points;
  }, [startingAmount, cagr, years]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = (props: any) => {
    const { payload, active } = props;
    if (!active || !payload || !payload.length) return null;
    const pt = payload[0]?.payload as ChartPoint | undefined;
    if (!pt) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[220px]">
        <div className="mb-2 pb-2 border-b border-gray-100">
          <div className="text-sm font-semibold text-gray-900">{translations.years}: {pt.year}</div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{translations.totalLabel}:</span>
            <span className="font-semibold text-blue-700">{pt.Total.toLocaleString('sv-SE')} {currency}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{translations.returnLabel}:</span>
            <span className="font-semibold text-emerald-700">{pt.Return.toLocaleString('sv-SE')} {currency}</span>
          </div>
        </div>
      </div>
    );
  };

  const handleCagrChange = (value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && onCagrChange) {
      onCagrChange(numValue / 100); // Convert percentage to decimal
    }
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow border border-white/20">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">{translations.chartTitle}</h3>
        <p className="text-xs text-gray-600">{translations.chartSubtitle}</p>
      </div>
      
      {/* CAGR Input */}
      {onCagrChange && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-center gap-4">
            <label className="text-sm font-medium text-gray-700">{translations.cagrInputLabel}</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={cagr !== null ? (cagr * 100).toFixed(1) : ''}
              onChange={(e) => handleCagrChange(e.target.value)}
              className="w-20 text-center border-2 border-gray-300 rounded px-2 py-1 focus:border-blue-500 focus:outline-none"
            />
            <span className="text-sm text-gray-600">%</span>
          </div>
        </div>
      )}
      <div className="relative w-full bg-gray-50/40 rounded-xl pt-5 pb-10 mb-4">
        <span className="absolute left-0 -top-2 text-xs text-gray-500">{`${translations.yAxisLabel.split('(')[0].trim()} (${currency})`}</span>
        <span className="absolute right-0 -bottom-2 text-xs text-gray-500">{translations.xAxisLabel}</span>
        <div className="w-full h-64">
          <ResponsiveContainer width="100%" height="100%">
            <RAreaChart data={data} margin={{ top: 8, right: 8, bottom: 24, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" interval="preserveStartEnd" tickMargin={8} />
              <YAxis hide />
              <Tooltip content={CustomTooltip} />
              <Area type="monotone" dataKey="Return" stroke="#10b981" fill="#10b98122" strokeWidth={2} />
              <Area type="monotone" dataKey="Total" stroke="#3b82f6" fill="#3b82f622" strokeWidth={2} />
            </RAreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}


